/* BLANC SPACE CUSTOM — homepage entry point for the shared device preference. */

class HomePhoneSelector extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized !== 'true') {
      this.dataset.initialized = 'true';

      this.openButton = this.querySelector('[data-home-device-open]');
      this.closeButton = this.querySelector('[data-home-device-close]');
      this.dialog = this.querySelector('[data-home-device-dialog]');
      this.tabs = this.querySelector('[data-home-device-tabs]');
      this.models = this.querySelector('[data-home-device-models]');
      this.options = this.querySelector('[data-home-device-options]');
      this.status = this.querySelector('[data-home-device-status]');
      this.entries = this.buildEntries();
    }

    // The desktop homepage composition relocates this section without cloning it.
    // Rebind after a standards-compliant disconnect/reconnect cycle so the picker
    // remains interactive while retaining one source of state and markup.
    this.openButton?.addEventListener('click', this.open);
    this.closeButton?.addEventListener('click', this.close);
    this.dialog?.addEventListener('click', this.handleBackdrop);
    this.dialog?.addEventListener('close', this.handleClose);
    window.addEventListener('blancspace:phone-model-change', this.handlePreferenceChange);
    window.addEventListener('blancspace:visual-phone-model-change', this.handleVisualPreferenceChange);

    this.updateLabel(this.getSavedModel());
    this.initializeVisualContext();
  }

  disconnectedCallback() {
    this.openButton?.removeEventListener('click', this.open);
    this.closeButton?.removeEventListener('click', this.close);
    this.dialog?.removeEventListener('click', this.handleBackdrop);
    this.dialog?.removeEventListener('close', this.handleClose);
    window.removeEventListener('blancspace:phone-model-change', this.handlePreferenceChange);
    window.removeEventListener('blancspace:visual-phone-model-change', this.handleVisualPreferenceChange);
  }

  buildEntries() {
    const entries = Array.from(this.options?.options || []).map((option) => ({
      model: option.value.trim(),
      brand: option.dataset.phoneBrand || 'other',
      source: option.dataset.phoneModelSource || 'legacy',
      url: option.dataset.collectionUrl || '',
    })).filter((entry) => entry.model);

    const routeScore = (entry) => entry.model.toLowerCase().match(/[a-z0-9]+/g)
      ?.filter((token) => entry.url.toLowerCase().includes(token)).length || 0;

    entries.sort((a, b) => (
      (a.source === 'metafield' ? 0 : 1) - (b.source === 'metafield' ? 0 : 1)
      || routeScore(b) - routeScore(a)
    ));

    const seen = new Set();
    return entries.filter((entry) => {
      const key = entry.model.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).sort((a, b) => {
      const generation = (model) => {
        if (/^iPhone Air$/i.test(model)) return 17;
        return Number(model.match(/(?:iPhone\s+|Pixel\s+|\bS)(\d+)/i)?.[1] || 0);
      };
      const tier = (model) => /pro max|ultra/i.test(model) ? 0
        : /\bpro\b/i.test(model) ? 1
          : /\bair\b|\bplus\b/i.test(model) ? 2
            : /\bflip\b|\bfold\b/i.test(model) ? 3 : 4;

      return generation(b.model) - generation(a.model)
        || tier(a.model) - tier(b.model)
        || a.model.localeCompare(b.model);
    });
  }

  getSavedModel() {
    if (window.BlancSpaceDevicePreference) return window.BlancSpaceDevicePreference.get();
    try { return localStorage.getItem('blancspace.phoneModel') || ''; } catch (error) { return ''; }
  }

  getPreferredBrand(savedModel = this.getSavedModel()) {
    const savedBrand = this.entries.find((entry) => entry.model === savedModel)?.brand;
    const detectedBrand = window.BlancSpaceDevicePreference?.getDetectedBrand?.();
    return savedBrand || detectedBrand || 'apple';
  }

  initializeVisualContext = async () => {
    const preference = window.BlancSpaceDevicePreference;
    if (!preference || this.getSavedModel()) return;

    const needsRefresh = preference.getVisualModel?.()
      && preference.getVisualModel() !== preference.getServerVisualModel?.();
    if (!needsRefresh) return;

    await preference.visualReady;
    this.refreshVisualCards();
  };

  handleVisualPreferenceChange = () => {
    this.refreshVisualCards();
  };

  refreshVisualCards = async () => {
    if (!this.isConnected || this.getSavedModel() || this.dataset.visualCardsReady === 'true') return;
    this.dataset.visualCardsReady = 'true';

    try {
      await this.refreshFeaturedCases();
    } catch (error) {
      this.dataset.visualCardsReady = 'false';
      console.warn('[Blanc Space] Detected-device product imagery could not be refreshed.', error);
    }
  };

  open = async () => {
    await window.BlancSpaceDevicePreference?.visualReady;
    const brandOrder = ['apple', 'samsung', 'google', 'other'];
    const availableBrands = new Set(this.entries.map((entry) => entry.brand));
    const brands = brandOrder.filter((brand) => availableBrands.has(brand));
    brands.push(...[...availableBrands].filter((brand) => !brandOrder.includes(brand)).sort());
    const savedModel = this.getSavedModel();
    const preferredBrand = this.getPreferredBrand(savedModel);
    const activeBrand = brands.includes(preferredBrand) ? preferredBrand : brands[0];

    this.tabs.replaceChildren(...brands.map((brand) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'menu-device-selector__tab';
      button.dataset.deviceBrand = brand;
      button.textContent = ({ apple: 'Apple', samsung: 'Samsung', google: 'Google', other: 'Other' })[brand] || brand;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', brand === activeBrand ? 'true' : 'false');
      button.addEventListener('click', () => this.renderModels(brand));
      return button;
    }));

    this.renderModels(activeBrand);
    this.openButton.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('home-phone-selector-open');
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    this.animateOpen();
    this.closeButton?.focus({ preventScroll: true });
  };

  animateOpen() {
    if (!window.matchMedia('screen and (max-width: 999px)').matches) return;
    this.dialog.getAnimations().forEach((animation) => animation.cancel());
    const animation = this.dialog.animate(
      [{ transform: 'translateY(110%)' }, { transform: 'translateY(0)' }],
      { duration: 400, easing: 'cubic-bezier(0.4, 0, 0.22, 1)', fill: 'both' }
    );
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) animation.finish();
    animation.finished.then(() => animation.cancel()).catch(() => {});
  }

  close = async () => {
    if (this.dataset.closing === 'true') return;
    const canClose = typeof this.dialog.close === 'function' && this.dialog.open;
    const shouldAnimate = canClose && window.matchMedia('screen and (max-width: 999px)').matches;

    if (shouldAnimate) {
      this.dataset.closing = 'true';
      this.dialog.getAnimations().forEach((animation) => animation.cancel());
      const animation = this.dialog.animate(
        [{ transform: 'translateY(0)' }, { transform: 'translateY(110%)' }],
        { duration: 300, easing: 'cubic-bezier(0.4, 0, 0.22, 1)', fill: 'both' }
      );
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) animation.finish();
      await animation.finished.catch(() => {});
    }

    if (canClose) this.dialog.close();
    else {
      this.dialog.removeAttribute('open');
      this.handleClose();
    }
  };

  handleBackdrop = (event) => {
    if (event.target === this.dialog) this.close();
  };

  handleClose = () => {
    this.dataset.closing = 'false';
    this.dialog?.getAnimations().forEach((animation) => animation.cancel());
    document.documentElement.classList.remove('home-phone-selector-open');
    this.openButton?.setAttribute('aria-expanded', 'false');
    this.openButton?.focus({ preventScroll: true });
  };

  handlePreferenceChange = (event) => {
    this.updateLabel(event.detail?.model || '');
  };

  renderModels(brand) {
    this.tabs.querySelectorAll('[role="tab"]').forEach((tab) => {
      tab.setAttribute('aria-selected', tab.dataset.deviceBrand === brand ? 'true' : 'false');
    });

    const savedModel = this.getSavedModel();
    this.models.replaceChildren(...this.entries.filter((entry) => entry.brand === brand).map((entry) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'menu-device-selector__model';
      button.textContent = entry.model;
      button.setAttribute('aria-current', entry.model === savedModel ? 'true' : 'false');
      button.addEventListener('click', () => this.selectModel(entry, button));
      return button;
    }));
  }

  async selectModel(entry, button) {
    if (this.dataset.updating === 'true') return;
    this.dataset.updating = 'true';
    button.disabled = true;
    this.openButton.setAttribute('aria-busy', 'true');
    this.status.textContent = `Saving ${entry.model}…`;

    try {
      if (window.BlancSpaceDevicePreference) {
        await window.BlancSpaceDevicePreference.set(entry.model, { source: 'menu', force: true });
      } else {
        try {
          localStorage.setItem('blancspace.phoneModel', entry.model);
          localStorage.setItem('blancspace.phoneModelSource', 'menu');
        } catch (error) {}
        window.dispatchEvent(new CustomEvent('blancspace:phone-model-change', {
          detail: { model: entry.model, source: 'menu', locked: true },
        }));
      }

      this.updateLabel(entry.model);

      // User testing (Aug 2026): after picking a model here, shoppers expect
      // to LAND on that phone's cases — matching the header menu picker —
      // not to be left on the homepage. In-place refresh remains the
      // fallback for menu entries without a collection URL.
      if (entry.url) {
        this.status.textContent = `Taking you to ${entry.model} cases…`;
        window.location.assign(entry.url);
        return;
      }

      this.close();
      await this.refreshFeaturedCases();
      this.status.textContent = `Showing cases for ${entry.model}.`;
    } catch (error) {
      console.warn('[Blanc Space] Homepage phone selection could not refresh product cards.', error);
      this.close();
      this.status.textContent = `${entry.model} is saved for browsing.`;
    } finally {
      button.disabled = false;
      this.openButton.removeAttribute('aria-busy');
      this.dataset.updating = 'false';
    }
  }

  async refreshFeaturedCases() {
    const sections = Array.from(document.querySelectorAll('.section--featured-collection-cases'));
    const contextModel = window.BlancSpaceDevicePreference?.getVisualModel?.() || '';
    const requestId = (this.featuredRefreshRequestId || 0) + 1;
    this.featuredRefreshRequestId = requestId;
    await Promise.all(sections.map(async (section) => {
      const sectionId = section.id.replace('shopify-section-', '');
      if (!sectionId) return;

      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      if (contextModel) url.searchParams.set('_bs_device_context', contextModel);
      const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Homepage case refresh failed (${response.status})`);

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.getElementById(section.id) || parsed.body.firstElementChild;
      if (!replacement) throw new Error('Homepage case refresh returned no section');
      if (requestId !== this.featuredRefreshRequestId) return;
      if (contextModel !== (window.BlancSpaceDevicePreference?.getVisualModel?.() || '')) return;
      section.replaceWith(replacement);
    }));
  }

  updateLabel(model) {
    const prompt = this.querySelector('[data-home-device-prompt]');
    const label = this.querySelector('[data-home-device-label]');
    if (prompt) prompt.textContent = model ? 'Selected phone' : 'Select your phone';
    if (label) label.textContent = model || 'Choose model';
  }
}

if (!customElements.get('home-phone-selector')) {
  customElements.define('home-phone-selector', HomePhoneSelector);
}
